package ui.user.detail

import androidx.compose.runtime.*
import common.frontend.api.Io
import common.frontend.api.Io.Permission.Companion.OTHERS_ADD
import common.frontend.api.Io.Permission.Companion.OTHERS_DELETE
import common.frontend.api.Io.Permission.Companion.OTHERS_EDIT
import common.frontend.api.Io.Permission.Companion.OTHERS_VIEW
import common.frontend.api.Io.Permission.Companion.OWN_ADD
import common.frontend.api.Io.Permission.Companion.OWN_DELETE
import common.frontend.api.Io.Permission.Companion.OWN_EDIT
import common.frontend.api.Io.Permission.Companion.OWN_VIEW
import provider.CommonAccountProvider
import provider.CommonDisplayProvider
import provider.LocalAccountProvider
import provider.LocalDisplayProvider
import tk.mallumo.compose.navigation.LocalNavigation
import tk.mallumo.compose.navigation.LocalNavigationArgs
import tk.mallumo.compose.navigation.Navigation
import tk.mallumo.compose.navigation.viewmodel.viewModel
import tools.EFieldList
import tools.EFieldRegularBoolean
import tools.EFieldTextString
import kotlin.reflect.KMutableProperty0


interface UserScope {

    val vm: UserVM
    val action: (UserVM.Action) -> Unit
    val account: CommonAccountProvider
    val display: CommonDisplayProvider

    val args: UserArgs
    val nav: Navigation
    val login: Io.User

    companion object {
        @Composable
        fun rememberScope(): UserScope {
            val nav = LocalNavigation.current
            val account = LocalAccountProvider.current
            val args = LocalNavigationArgs.current.rememberArgs(UserArgs::class)
            val vm = nav.viewModel(UserVM::class, args.userId.toString())
            val action = UserVM.Action.rememberAction(vm)
            val display = LocalDisplayProvider.current
            return remember {
                object : UserScope {
                    override val vm: UserVM
                        get() = vm
                    override val action: (UserVM.Action) -> Unit
                        get() = action
                    override val account: CommonAccountProvider
                        get() = account
                    override val display: CommonDisplayProvider
                        get() = display
                    override val args: UserArgs
                        get() = args
                    override val nav: Navigation
                        get() = nav
                    override val login: Io.User
                        get() = account.current!!
                }.also {
                    vm.init(it)
                }
            }
        }
    }

    class Wrapper(
        val canEdit: Boolean,
        val canDelete: Boolean,
        val canPermissionEditOther: Boolean,
        val canPermissionEditOwn: Boolean,
        val login: Io.User,
        val title: Pair<String, String>,
        override val data: Io.User,
        override val key: String = data.id.toString(),
    ) : EFieldList.Entry<Io.User> {

        private var origin = data.copy()

        val permissionOwn: WrapperPermission = WrapperPermission(
            login = login,
            data = { data },
            isOwn = true
        )

        val permissionOther: WrapperPermission = WrapperPermission(
            login = login,
            data = { data },
            isOwn = false
        )

        val name = EFieldTextString(
            property = data::name,
            condition = { it.trim().takeIf { it.length > 3 } },
            onChange = { makeChange() },
            onErr = { makeChange() })

        var change by mutableStateOf(false)
            private set

        fun updateOrigin() {
            origin = data.copy()
            makeChange()
        }

        private fun makeChange() {
            change = if (name.err) false
            else if (origin.name != data.name) true
            else if (permissionOwn.changed) true
            else if (permissionOther.changed) true
            else false
        }
    }

    class WrapperPermission(
        private val login: Io.User,
        private val data: () -> Io.User,
        private val isOwn: Boolean
    ) {


        lateinit var user: WrapperPermissionItem
            private set

        lateinit var userPermission: WrapperPermissionItem
            private set
        lateinit var company: WrapperPermissionItem
            private set
        lateinit var location: WrapperPermissionItem
            private set
        lateinit var locationEmergency: WrapperPermissionItem
            private set
        lateinit var deviceInfo: WrapperPermissionItem
            private set
        lateinit var deviceDisplay: WrapperPermissionItem
            private set
        lateinit var deviceMode: WrapperPermissionItem
            private set
        lateinit var server: WrapperPermissionItem
            private set
        lateinit var deviceActions: WrapperPermissionItem
            private set
        lateinit var resources: WrapperPermissionItem
            private set
        lateinit var campaign: WrapperPermissionItem
            private set
        lateinit var deviceShare: WrapperPermissionItem
            private set
        lateinit var translation: WrapperPermissionItem
            private set


        lateinit var reportSystem: WrapperPermissionItem
            private set
        lateinit var reportLayerTop: WrapperPermissionItem
            private set
        lateinit var reportLayerBottom: WrapperPermissionItem
            private set

        lateinit var license: WrapperPermissionItem
            private set
        lateinit var hardware: WrapperPermissionItem
            private set

        var changed by mutableStateOf(false)
            private set

        var permissionGroups by mutableStateOf(listOf<PermissionGroup>())
            private set

        private val allPermissions
            get() = listOf(
                user,
                userPermission,
                company,
                location,
                locationEmergency,
                deviceInfo,
                deviceDisplay,
                deviceMode,
                server,
                deviceActions,
                resources,
                campaign,
                deviceShare,
                translation,
                reportSystem,
                reportLayerTop,
                reportLayerBottom,
                license,
                hardware
            )

        private val onChange: () -> Unit = {
            changed = allPermissions.any { it.changed }
        }

        init {
            updateOrigin()
        }

        fun applyChanges() {
            allPermissions.forEach { it.applyChanges() }
        }


        fun updateOrigin() {
            user = WrapperPermissionItem(
                isCompanyTheSame = login.company.id == data().company.id,
                visibilityMask = OWN_VIEW + OWN_ADD + OWN_EDIT + OWN_DELETE
                    + OTHERS_VIEW + OTHERS_ADD + OTHERS_EDIT + OTHERS_DELETE,
                property = data().permission::user,
                loginPermission = login.permission.user,
                isOwn = isOwn, onChange = onChange
            )
            userPermission = WrapperPermissionItem(
                isCompanyTheSame = login.company.id == data().company.id,
                visibilityMask = OWN_VIEW + OWN_EDIT
                    + OTHERS_VIEW + OTHERS_EDIT,
                property = data().permission::user_permission,
                loginPermission = login.permission.user_permission,
                isOwn = isOwn, onChange = onChange
            )
            company = WrapperPermissionItem(
                isCompanyTheSame = login.company.id == data().company.id,
                visibilityMask = OWN_VIEW + OWN_EDIT +
                    OTHERS_VIEW + OTHERS_ADD + OTHERS_EDIT + OTHERS_DELETE,
                property = data().permission::company,
                loginPermission = login.permission.company,
                isOwn = isOwn, onChange = onChange
            )
            location =
                WrapperPermissionItem(
                    isCompanyTheSame = login.company.id == data().company.id,
                    visibilityMask = OWN_VIEW + OWN_ADD + OWN_EDIT + OWN_DELETE
                        + OTHERS_VIEW + OTHERS_ADD + OTHERS_EDIT + OTHERS_DELETE,
                    property = data().permission::location,
                    loginPermission = login.permission.location,
                    isOwn = isOwn, onChange = onChange
                )
            locationEmergency =
                WrapperPermissionItem(
                    isCompanyTheSame = login.company.id == data().company.id,
                    visibilityMask = OWN_VIEW + OWN_ADD + OWN_EDIT
                        + OTHERS_VIEW + OTHERS_ADD + OTHERS_EDIT,
                    property = data().permission::location_emergency,
                    loginPermission = login.permission.location_emergency,
                    isOwn = isOwn, onChange = onChange
                )
            deviceInfo = WrapperPermissionItem(
                isCompanyTheSame = login.company.id == data().company.id,
                visibilityMask = OWN_VIEW + OWN_ADD + OWN_EDIT + OWN_DELETE
                    + OTHERS_VIEW + OTHERS_ADD + OTHERS_EDIT + OTHERS_DELETE,
                property = data().permission::device_info,
                loginPermission = login.permission.device_info,
                isOwn = isOwn, onChange = onChange
            )
            deviceDisplay = WrapperPermissionItem(
                isCompanyTheSame = login.company.id == data().company.id,
                visibilityMask = OWN_VIEW + OWN_EDIT
                    + OTHERS_VIEW + OTHERS_EDIT,
                property = data().permission::device_display,
                loginPermission = login.permission.device_display,
                isOwn = isOwn, onChange = onChange
            )
            deviceShare = WrapperPermissionItem(
                isCompanyTheSame = login.company.id == data().company.id,
                visibilityMask = 0,
                property = data().permission::device_share,
                loginPermission = login.permission.device_share,
                isOwn = isOwn, onChange = onChange
            )
            deviceMode = WrapperPermissionItem(
                isCompanyTheSame = login.company.id == data().company.id,
                visibilityMask = OWN_VIEW + OWN_EDIT + OWN_ADD + OWN_DELETE
                    + OTHERS_VIEW + OTHERS_EDIT + OTHERS_ADD + OTHERS_DELETE,
                property = data().permission::device_layer_bottom,
                loginPermission = login.permission.device_layer_bottom,
                isOwn = isOwn, onChange = onChange
            )
            server = WrapperPermissionItem(
                isCompanyTheSame = login.company.id == data().company.id,
                visibilityMask = OWN_VIEW + OWN_EDIT +
                    OTHERS_VIEW + OTHERS_ADD + OTHERS_EDIT + OTHERS_DELETE,
                property = data().permission::server,
                loginPermission = login.permission.server,
                isOwn = isOwn, onChange = onChange
            )
            deviceActions = WrapperPermissionItem(
                isCompanyTheSame = login.company.id == data().company.id,
                visibilityMask = OWN_VIEW + OTHERS_VIEW,
                property = data().permission::device_actions,
                loginPermission = login.permission.device_actions,
                isOwn = isOwn, onChange = onChange
            )
            resources = WrapperPermissionItem(
                isCompanyTheSame = login.company.id == data().company.id,
                visibilityMask = OWN_VIEW + OWN_ADD + OWN_EDIT + OWN_DELETE
                    + OTHERS_VIEW + OTHERS_ADD + OTHERS_EDIT + OTHERS_DELETE,
                property = data().permission::resource,
                loginPermission = login.permission.resource,
                isOwn = isOwn, onChange = onChange
            )
            campaign = WrapperPermissionItem(
                isCompanyTheSame = login.company.id == data().company.id,
                visibilityMask = OWN_VIEW + OWN_ADD + OWN_EDIT + OWN_DELETE
                    + OTHERS_VIEW + OTHERS_ADD + OTHERS_EDIT + OTHERS_DELETE,
                property = data().permission::device_layer_top,
                loginPermission = login.permission.device_layer_top,
                isOwn = isOwn, onChange = onChange
            )
            translation = WrapperPermissionItem(
                isCompanyTheSame = login.company.id == data().company.id,
                visibilityMask = OWN_VIEW + OWN_EDIT + OWN_ADD,
                property = data().permission::translation,
                loginPermission = login.permission.translation,
                isOwn = isOwn, onChange = onChange
            )

            reportSystem = WrapperPermissionItem(
                isCompanyTheSame = login.company.id == data().company.id,
                visibilityMask = OWN_VIEW + OTHERS_VIEW,
                property = data().permission::report_system,
                loginPermission = login.permission.report_system,
                isOwn = isOwn, onChange = onChange
            )
            reportLayerTop = WrapperPermissionItem(
                isCompanyTheSame = login.company.id == data().company.id,
                visibilityMask = OWN_VIEW + OTHERS_VIEW,
                property = data().permission::report_layer_top,
                loginPermission = login.permission.report_layer_top,
                isOwn = isOwn, onChange = onChange
            )
            reportLayerBottom = WrapperPermissionItem(
                isCompanyTheSame = login.company.id == data().company.id,
                visibilityMask = 0,
                property = data().permission::report_layer_bottom,
                loginPermission = login.permission.report_layer_bottom,
                isOwn = isOwn, onChange = onChange
            )

            license = WrapperPermissionItem(
                isCompanyTheSame = login.company.id == data().company.id,
                visibilityMask = OWN_VIEW + OWN_EDIT + OWN_ADD + OWN_DELETE +
                    OTHERS_VIEW + OTHERS_ADD + OTHERS_EDIT + OTHERS_DELETE,
                property = data().permission::license,
                loginPermission = login.permission.license,
                isOwn = isOwn, onChange = onChange
            )

            hardware = WrapperPermissionItem(
                isCompanyTheSame = login.company.id == data().company.id,
                visibilityMask = OWN_VIEW + OWN_EDIT + OWN_ADD + OWN_DELETE +
                        OTHERS_VIEW,
                property = data().permission::hardware,
                loginPermission = login.permission.hardware,
                isOwn = isOwn, onChange = onChange
            )

            permissionGroups = rebuildPermissionGroups(login, data())
            onChange()
        }
    }

    class WrapperPermissionItem(
        var isCompanyTheSame: Boolean,
        val property: KMutableProperty0<Int>,
        val isOwn: Boolean,
        val visibilityMask: Int,
        val loginPermission: Int,
        val onChange: () -> Unit
    ) {
        val maskView = (OWN_VIEW.takeIf { isOwn } ?: OTHERS_VIEW)
        val maskAdd = (OWN_ADD.takeIf { isOwn } ?: OTHERS_ADD)
        val maskEdit = (OWN_EDIT.takeIf { isOwn } ?: OTHERS_EDIT)
        val maskDelete = (OWN_DELETE.takeIf { isOwn } ?: OTHERS_DELETE)

        private val maskViewReversed = (OTHERS_VIEW.takeIf { isOwn } ?: OWN_VIEW)
        private val maskAddReversed = (OTHERS_ADD.takeIf { isOwn } ?: OWN_ADD)
        private val maskEditReversed = (OTHERS_EDIT.takeIf { isOwn } ?: OWN_EDIT)
        private val maskDeleteReversed = (OTHERS_DELETE.takeIf { isOwn } ?: OWN_DELETE)

        val loginMaskView = if (isCompanyTheSame) maskView else maskViewReversed
        val loginMaskAdd = if (isCompanyTheSame) maskView else maskAddReversed
        val loginMaskEdit = if (isCompanyTheSame) maskView else maskEditReversed
        val loginMaskDelete = if (isCompanyTheSame) maskView else maskDeleteReversed

        private val defaultView = property.get() and maskView > 0
        private val defaultAdd = property.get() and maskAdd > 0
        private val defaultEdit = property.get() and maskEdit > 0
        private val defaultDelete = property.get() and maskDelete > 0

        private var _view: Boolean = defaultView
        private var _add: Boolean = defaultAdd
        private var _edit: Boolean = defaultEdit
        private var _delete: Boolean = defaultDelete

        val view = EFieldRegularBoolean(
            property = this::_view,
            onChange = { onChange() })

        val add = EFieldRegularBoolean(
            property = this::_add,
            onChange = { onChange() })

        val edit = EFieldRegularBoolean(
            property = this::_edit,
            onChange = { onChange() })

        val delete = EFieldRegularBoolean(
            property = this::_delete,
            onChange = { onChange() })

        val changed
            get() = defaultView != _view ||
                defaultAdd != _add ||
                defaultEdit != _edit ||
                defaultDelete != _delete

        fun applyGroupChanges(newPropertyMask: Int) {
            view.set(newPropertyMask and maskView > 0)
            add.set(newPropertyMask and maskAdd > 0)
            edit.set(newPropertyMask and maskEdit > 0)
            delete.set(newPropertyMask and maskDelete > 0)
        }

        fun applyChanges() {
            fun propertySetup(newStateGranted: Boolean, mask: Int) {
                val oldStateGranted = (property.get() and mask) > 0
                if (oldStateGranted != newStateGranted) {
                    if (newStateGranted) {
                        property.set(property.get() + mask)
                    } else {
                        property.set(property.get() - mask)
                    }
                }
            }
            propertySetup(_view, maskView)
            propertySetup(_edit, maskEdit)
            propertySetup(_add, maskAdd)
            propertySetup(_delete, maskDelete)
        }
    }
}
